import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import styled from 'styled-components';
import { makeLogout } from 'src/utils/auth';
import { TokenInfo, RefreshEvent, TEST_MODE, TOKEN_CHECK_INTERVAL } from './types';

interface DebugPanelProps {
  tokenInfo: TokenInfo;
  refreshCount: number;
  lastRefresh: RefreshEvent | null;
  refreshLog: RefreshEvent[];
  error: string | null;
  isRefreshing: boolean;
  onForceRefresh: () => void;
  lastActivity: number;
}

const Panel = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  width: 400px;
  background: rgba(0, 0, 0, 0.95);
  color: #0f0;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 11px;
  padding: 12px;
  border-radius: 8px;
  z-index: 99999;
  max-height: 80vh;
  overflow-y: auto;

  .section {
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #333;
  }
  .section-title {
    color: #0ff;
    font-weight: bold;
    margin-bottom: 6px;
  }
  .row {
    display: flex;
    justify-content: space-between;
    padding: 3px 0;
  }
  .label {
    color: #888;
  }
  .value {
    color: #0f0;
  }
  .error {
    color: #f55;
  }
  .warning {
    color: #fa0;
  }
  .refresh {
    color: #0ff;
  }
  .btn {
    background: #333;
    color: #0ff;
    border: 1px solid #0ff;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 10px;
    margin: 4px 4px 4px 0;
    &:hover {
      background: #0ff;
      color: #000;
    }
    &:disabled {
      opacity: 0.5;
    }
  }
  .btn-danger {
    background: #500;
    border-color: #f55;
    color: #f55;
  }
  .log-entry {
    font-size: 9px;
    padding: 2px 0;
    border-bottom: 1px solid #111;
  }
`;

const formatDuration = (ms: number): string => {
  if (ms <= 0) return 'EXPIRED';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
};

const formatTime = (ts: number): string => new Date(ts).toLocaleTimeString();

const DebugPanel: React.FC<DebugPanelProps> = ({
  tokenInfo,
  refreshCount,
  lastRefresh,
  refreshLog,
  error,
  isRefreshing,
  onForceRefresh,
  lastActivity,
}) => {
  const { isAuthenticated, isLoading, logout } = useAuth0();
  const [visible, setVisible] = useState(true);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  const handleLogout = () => {
    makeLogout({ noEvents: true });
    logout({ logoutParams: { returnTo: `${location.protocol}//${location.host}/auth` } });
  };

  if (!visible) {
    return (
      <button
        onClick={() => setVisible(true)}
        style={{
          position: 'fixed',
          bottom: 20,
          left: 20,
          background: '#000',
          color: '#0f0',
          border: '1px solid #0f0',
          padding: '6px 10px',
          borderRadius: 4,
          cursor: 'pointer',
          zIndex: 99999,
          fontFamily: 'monospace',
          fontSize: 10,
        }}
      >
        🔧 {refreshCount > 0 && <span style={{ color: '#0ff' }}>({refreshCount})</span>}
      </button>
    );
  }

  const { accessExp, idExp } = tokenInfo;
  const accessRemaining = accessExp ? accessExp - now : null;
  const idRemaining = idExp ? idExp - now : null;
  const inactivityTime = now - lastActivity;

  return (
    <Panel>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ color: '#0ff', fontWeight: 'bold' }}>
          🔐 Session Manager {TEST_MODE && <span style={{ color: '#fa0' }}>[TEST MODE]</span>}
        </span>
        <button
          onClick={() => setVisible(false)}
          style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer' }}
        >
          ×
        </button>
      </div>

      {/* Auth State */}
      <div className="section">
        <div className="section-title">Auth State</div>
        <div className="row">
          <span className="label">Status:</span>
          <span className={isLoading ? 'warning' : isAuthenticated ? 'value' : 'error'}>
            {isLoading ? 'Loading...' : isAuthenticated ? 'Authenticated' : 'Not Auth'}
          </span>
        </div>
        <div className="row">
          <span className="label">Inactive for:</span>
          <span className={inactivityTime > 30 * 60 * 1000 ? 'warning' : 'value'}>
            {formatDuration(inactivityTime)}
          </span>
        </div>
      </div>

      {/* Tokens */}
      <div className="section">
        <div className="section-title">Tokens</div>
        <div className="row">
          <span className="label">Access Token:</span>
          <span
            className={
              accessRemaining && accessRemaining < 30000
                ? 'error'
                : accessRemaining && accessRemaining < 60000
                  ? 'warning'
                  : 'value'
            }
          >
            {accessRemaining !== null ? formatDuration(accessRemaining) : 'N/A'}
            {accessExp && <span style={{ color: '#666', marginLeft: 6 }}>({formatTime(accessExp)})</span>}
          </span>
        </div>
        <div className="row">
          <span className="label">ID Token:</span>
          <span
            className={
              idRemaining && idRemaining < 30000 ? 'error' : idRemaining && idRemaining < 60000 ? 'warning' : 'value'
            }
          >
            {idRemaining !== null ? formatDuration(idRemaining) : 'N/A'}
            {idExp && <span style={{ color: '#666', marginLeft: 6 }}>({formatTime(idExp)})</span>}
          </span>
        </div>
      </div>

      {/* Refresh Stats */}
      <div className="section">
        <div className="section-title">Refresh Stats</div>
        <div className="row">
          <span className="label">Total Refreshes:</span>
          <span className="refresh">{refreshCount}</span>
        </div>
        {lastRefresh && (
          <div className="row">
            <span className="label">Last Refresh:</span>
            <span className="refresh">{formatTime(lastRefresh.time)}</span>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="section">
          <div className="row">
            <span className="label">Error:</span>
            <span className="error" style={{ fontSize: 9, wordBreak: 'break-all' }}>
              {error}
            </span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ marginBottom: 12 }}>
        <button className="btn" onClick={onForceRefresh} disabled={isRefreshing}>
          {isRefreshing ? 'Refreshing...' : 'Force Refresh'}
        </button>
        <button className="btn btn-danger" onClick={handleLogout}>
          Logout
        </button>
      </div>

      {/* Refresh Log */}
      {refreshLog.length > 0 && (
        <div className="section" style={{ borderBottom: 'none' }}>
          <div className="section-title">📜 Refresh Log</div>
          <div style={{ maxHeight: 100, overflowY: 'auto' }}>
            {refreshLog.map((event, i) => (
              <div key={i} className="log-entry">
                <span
                  style={{
                    color: '#0f0',
                  }}
                >
                  {formatTime(event.time)}
                </span>
                <span style={{ color: '#666', marginLeft: 6 }}>
                  {formatTime(event.oldExp)} → {formatTime(event.newExp)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ fontSize: 9, color: '#555', marginTop: 8 }}>
        {formatTime(now)} • Checking every {TOKEN_CHECK_INTERVAL / 1000}s
      </div>
    </Panel>
  );
};

export default DebugPanel;
